import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Vote,
  ThumbsUp,
  ThumbsDown,
  Undo2,
  CircleCheck,
  CircleX,
  Clock,
  Gavel,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { api, describeOrgProposalError, type OrganizationRole, type OrgProposal, type OrgProposalStatus } from '../lib/api';
import { normalizeOrgMembersResponse } from '../lib/orgMembersPaging';
import { Card } from './Card';
import { ConfirmModal } from './ConfirmModal';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';

// ─────────────────────────────────────────────────────────────────────────────
// OrgProposalsPanel — PR4 governance UI for the PR2 voting engine.
//
// Owner-gated (mirrors the backend: GET/POST /proposals requires ORG_OWNER or
// a platform founder). Voting itself is stricter than the usual founder
// bypass: a founder who is NOT also a current owner cannot cast a board vote
// (backend: "no founder bypass here... a founder overrides via resolve, never
// by casting a board vote themselves") — so Yes/No is gated on
// currentRole === 'ORG_OWNER' specifically, while Execute/Cancel is
// founder-only. A founder who also holds an owner seat sees both.
// ─────────────────────────────────────────────────────────────────────────────

function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
}

function neededVotes(quorumRule: OrgProposal['quorumRule'], ownerCount: number): number {
  return quorumRule === 'unanimous' ? ownerCount : Math.floor(ownerCount / 2) + 1;
}

function statusVisuals(status: OrgProposalStatus) {
  switch (status) {
    case 'EXECUTED':  return { Icon: CircleCheck, cls: 'text-emerald-300' };
    case 'REJECTED':  return { Icon: CircleX,     cls: 'text-red-300' };
    case 'EXPIRED':   return { Icon: Clock,       cls: 'text-iron-400' };
    case 'WITHDRAWN': return { Icon: Undo2,       cls: 'text-iron-400' };
    default:          return { Icon: Vote,        cls: 'text-iron-400' };
  }
}

// ── ProposalRow — per-row sub-component (unexported) ───────────────────────────

type ProposalRowProps = {
  proposal: OrgProposal;
  orgId: string;
  index: number;
  currentUserId: string;
  isFounder: boolean;
  canVote: boolean;
  targetName: string;
  proposerName: string;
  onResolveRequest: (proposal: OrgProposal, outcome: 'execute' | 'cancel') => void;
};

function ProposalRow({
  proposal,
  orgId,
  index,
  currentUserId,
  isFounder,
  canVote,
  targetName,
  proposerName,
  onResolveRequest,
}: ProposalRowProps) {
  const qc = useQueryClient();

  // ── ALL hooks before any early return ─────────────────────────────────────
  const [voteError, setVoteError] = useState('');

  const voteMut = useMutation({
    mutationFn: (vote: 'yes' | 'no') => api.voteOnOrgProposal(orgId, proposal.proposalId, vote),
    onSuccess: () => {
      setVoteError('');
      qc.invalidateQueries({ queryKey: ['orgProposals', orgId] });
    },
    onError: (e: Error) => setVoteError(describeOrgProposalError(e)),
  });

  const withdrawMut = useMutation({
    mutationFn: () => api.withdrawOrgProposal(orgId, proposal.proposalId),
    onSuccess: () => {
      setVoteError('');
      qc.invalidateQueries({ queryKey: ['orgProposals', orgId] });
    },
    onError: (e: Error) => setVoteError(describeOrgProposalError(e)),
  });

  const myVote = proposal.votes.find(v => v.voterDiscordId === currentUserId)?.vote ?? null;
  const needed = neededVotes(proposal.quorumRule, proposal.tally.ownerCount);
  const canWithdraw = proposal.proposerDiscordId === currentUserId || isFounder;
  const busy = voteMut.isPending || withdrawMut.isPending;

  const onWithdraw = () => {
    if (!window.confirm(`Withdraw the vote to remove ${targetName} as an owner?`)) return;
    withdrawMut.mutate();
  };

  return (
    <div
      className={`${SETTLE} border border-iron-800/60 bg-iron-950 px-4 py-3`}
      style={settleDelay(index)}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-display text-sm font-black uppercase tracking-wide text-white">
            <Vote className="h-3.5 w-3.5 text-forge-400" strokeWidth={2.5} />
            Remove {targetName} as an owner
          </p>
          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-iron-500">
            proposed by {proposerName} · opened {formatDate(proposal.createdAt)} · expires {formatDate(proposal.expiresAt)}
          </p>
        </div>
        <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-iron-400">
          {proposal.tally.yes} yes · {proposal.tally.no} no · needs {needed} of {proposal.tally.ownerCount}
        </span>
      </div>

      {voteError && (
        <div className="mt-2 flex items-center gap-2 text-xs text-red-300">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
          {voteError}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {canVote && (
          <>
            <button
              type="button"
              onClick={() => voteMut.mutate('yes')}
              disabled={busy}
              className={cn(
                `${MORPH} flex items-center gap-1.5 border px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider transition-colors disabled:opacity-50`,
                myVote === 'yes'
                  ? 'border-emerald-500/60 bg-emerald-950/60 text-emerald-100'
                  : 'border-iron-700 bg-iron-950 text-iron-300 hover:border-emerald-600/60 hover:text-emerald-200'
              )}
            >
              {voteMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <ThumbsUp className="h-3 w-3" strokeWidth={2.5} />}
              Yes
            </button>
            <button
              type="button"
              onClick={() => voteMut.mutate('no')}
              disabled={busy}
              className={cn(
                `${MORPH} flex items-center gap-1.5 border px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider transition-colors disabled:opacity-50`,
                myVote === 'no'
                  ? 'border-red-500/60 bg-red-950/60 text-red-100'
                  : 'border-iron-700 bg-iron-950 text-iron-300 hover:border-red-600/60 hover:text-red-200'
              )}
            >
              {voteMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <ThumbsDown className="h-3 w-3" strokeWidth={2.5} />}
              No
            </button>
            <span className="font-mono text-[10px] uppercase tracking-wider text-iron-500">
              you: {myVote === 'yes' ? 'Yes' : myVote === 'no' ? 'No' : 'not voted'}
            </span>
          </>
        )}

        {canWithdraw && (
          <button
            type="button"
            onClick={onWithdraw}
            disabled={busy}
            className={`btn-ghost ${MORPH} ml-auto inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-black uppercase tracking-wider disabled:opacity-50`}
          >
            {withdrawMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" strokeWidth={2.5} />}
            Withdraw
          </button>
        )}

        {isFounder && (
          <>
            <button
              type="button"
              onClick={() => onResolveRequest(proposal, 'execute')}
              className={cn(
                `${MORPH} inline-flex items-center gap-1.5 border border-forge-500/40 bg-forge-500/10 px-2 py-1 font-display text-[10px] font-black uppercase tracking-wider text-forge-300 transition-colors hover:border-forge-400 hover:text-forge-100`,
                !canWithdraw && 'ml-auto'
              )}
            >
              <Gavel className="h-3 w-3" strokeWidth={2.5} />
              Execute
            </button>
            <button
              type="button"
              onClick={() => onResolveRequest(proposal, 'cancel')}
              className={`${MORPH} inline-flex items-center gap-1.5 border border-iron-700 bg-iron-950 px-2 py-1 font-display text-[10px] font-black uppercase tracking-wider text-iron-300 transition-colors hover:border-iron-500 hover:text-iron-100`}
            >
              <CircleX className="h-3 w-3" strokeWidth={2.5} />
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── OrgProposalsPanel — exported ───────────────────────────────────────────────

export function OrgProposalsPanel({
  orgId,
  currentUserId,
  isFounder,
  currentRole,
}: {
  orgId: string;
  currentUserId: string;
  isFounder: boolean;
  currentRole: OrganizationRole;
}) {
  const qc = useQueryClient();

  // ── ALL hooks declared before any early return ────────────────────────────
  const [resolveTarget, setResolveTarget] = useState<{ proposal: OrgProposal; outcome: 'execute' | 'cancel' } | null>(null);
  const [resolveError, setResolveError] = useState('');

  const canView = currentRole === 'ORG_OWNER' || isFounder;
  // Backend: no founder bypass for casting a board vote — must actually hold
  // an ORG_OWNER seat right now.
  const canVote = currentRole === 'ORG_OWNER';

  const { data, isLoading } = useQuery({
    queryKey: ['orgProposals', orgId],
    queryFn: () => api.orgProposals(orgId),
    enabled: canView,
    refetchInterval: 20_000,
  });

  // Shared cache key with OrgMembersPanel/OrgOwnerBoardPanel — dedupes when
  // mounted together, purely for gamertag display here. Page one only (both-
  // shapes tolerant via normalizeOrgMembersResponse); envelope's cursor/
  // importStatus are irrelevant to a display-name lookup.
  const { data: pageOneRaw } = useQuery({
    queryKey: ['orgMembers', orgId],
    queryFn: () => api.orgMembers(orgId),
    enabled: canView,
  });
  const members = normalizeOrgMembersResponse(pageOneRaw).members;
  const nameById = new Map(members.map(m => [m.discordId, m.displayName ?? m.gamertag ?? shortId(m.discordId)]));
  const nameFor = (discordId: string) => nameById.get(discordId) ?? shortId(discordId);

  const resolveMut = useMutation({
    mutationFn: ({ proposalId, outcome }: { proposalId: string; outcome: 'execute' | 'cancel' }) =>
      api.resolveOrgProposal(orgId, proposalId, outcome),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgProposals', orgId] });
      setResolveTarget(null);
      setResolveError('');
    },
    onError: (e: Error) => {
      setResolveError(describeOrgProposalError(e));
      setResolveTarget(null);
    },
  });

  // Guard: non-owners see nothing (double safety — mount in MyOrg is already gated)
  if (!canView) return null;

  const proposals = data?.proposals ?? [];
  const open = proposals.filter(p => p.status === 'OPEN');
  const resolved = proposals.filter(p => p.status !== 'OPEN');

  return (
    <Card>
      {/* ── Header ── */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
          <Vote className="h-4 w-4 text-forge-400" strokeWidth={2.5} />
          Governance Proposals
          {!isLoading && open.length > 0 && (
            <span className="ml-1 font-mono text-[10px] font-normal text-iron-500">{open.length} open</span>
          )}
        </h2>
      </div>

      {resolveError && (
        <div className="mb-3 flex items-center gap-2 rounded bg-red-950/50 px-3 py-2 text-xs text-red-300">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
          {resolveError}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 py-8 text-iron-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-xs">Loading proposals…</span>
        </div>
      )}

      {!isLoading && open.length === 0 && (
        <p className="py-6 text-center text-xs text-iron-500">No open votes.</p>
      )}

      {!isLoading && open.length > 0 && (
        <div className="space-y-2">
          {open.map((p, i) => (
            <ProposalRow
              key={p.proposalId}
              proposal={p}
              orgId={orgId}
              index={i}
              currentUserId={currentUserId}
              isFounder={isFounder}
              canVote={canVote}
              targetName={nameFor(p.targetDiscordId)}
              proposerName={nameFor(p.proposerDiscordId)}
              onResolveRequest={(proposal, outcome) => setResolveTarget({ proposal, outcome })}
            />
          ))}
        </div>
      )}

      {/* ── Resolved history ── */}
      {!isLoading && resolved.length > 0 && (
        <div className="mt-6 border-t border-iron-800/60 pt-4">
          <h3 className="mb-3 flex items-center gap-2 font-display text-[10px] font-black uppercase tracking-[0.22em] text-iron-400">
            History
            <span className="font-mono text-[10px] font-normal text-iron-500">{resolved.length}</span>
          </h3>
          <div className="space-y-1">
            {resolved.map(p => {
              const { Icon, cls } = statusVisuals(p.status);
              return (
                <div
                  key={p.proposalId}
                  className="flex items-center justify-between border border-iron-900/60 bg-iron-950 px-4 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="truncate font-display text-xs font-black uppercase tracking-wide text-iron-300">
                      Remove {nameFor(p.targetDiscordId)} as an owner
                    </p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[10px] text-iron-500">
                      <span className={cn('inline-flex items-center gap-1 font-bold uppercase tracking-wider', cls)}>
                        <Icon className="h-2.5 w-2.5" strokeWidth={2.5} />
                        {p.status}
                      </span>
                      {p.resolvedAt && <span>{formatDate(p.resolvedAt)}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Founder execute/cancel modal ── */}
      {resolveTarget && (
        <ConfirmModal
          tone={resolveTarget.outcome === 'execute' ? 'danger' : 'warn'}
          icon={resolveTarget.outcome === 'execute' ? Gavel : CircleX}
          title={
            resolveTarget.outcome === 'execute'
              ? `Force-execute: remove ${nameFor(resolveTarget.proposal.targetDiscordId)} as an owner?`
              : `Cancel the vote to remove ${nameFor(resolveTarget.proposal.targetDiscordId)}?`
          }
          body={
            resolveTarget.outcome === 'execute'
              ? 'This bypasses the board tally entirely — a platform founder override. The target loses their owner seat immediately.'
              : 'This rejects the proposal outright, regardless of the current tally.'
          }
          confirmLabel={resolveTarget.outcome === 'execute' ? 'Execute' : 'Cancel vote'}
          confirmIcon={resolveTarget.outcome === 'execute' ? Gavel : CircleX}
          pending={resolveMut.isPending}
          onConfirm={() => resolveMut.mutate({ proposalId: resolveTarget.proposal.proposalId, outcome: resolveTarget.outcome })}
          onCancel={() => setResolveTarget(null)}
        />
      )}
    </Card>
  );
}
